import React, { useMemo, useState } from "react";
import { CorpHeader } from "@/components/CorpHeader";
import { useLang } from "@/lib/i18n";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Download, Link2, Mail, Share2, MessageCircle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const VIDEOS = [
  {
    lang: "en",
    titleKey: "admin.promo.card_title_en",
    duration: "1:34",
    size: "~5.3 MB",
    resolution: "MP4 · 1280×720",
    filename: "relax-mis-promo-en.mp4",
    message:
      "Relax-MIS: replace paper with digital management information. Watch: ",
  },
  {
    lang: "th",
    titleKey: "admin.promo.card_title_th",
    duration: "1:28",
    size: "~4.7 MB",
    resolution: "MP4 · 1280×720",
    filename: "relax-mis-promo-th.mp4",
    message:
      "Relax-MIS: เปลี่ยนงานเอกสารกระดาษเป็นระบบดิจิทัลพร้อมรายงานสำหรับการบริหาร ดูวิดีโอ: ",
  },
];

function ShareButtons({ shareUrl, message, tLabel }) {
  const body = encodeURIComponent(`${message}${shareUrl}`);
  const subject = encodeURIComponent("Relax-MIS promo video");
  return (
    <div className="flex flex-wrap gap-2 pt-1">
      <Button
        asChild
        variant="outline"
        size="sm"
        className="gap-1.5"
        data-testid={`promo-share-email-${tLabel}`}
      >
        <a href={`mailto:?subject=${subject}&body=${body}`}>
          <Mail className="w-3.5 h-3.5" />
          Email
        </a>
      </Button>
      <Button
        asChild
        variant="outline"
        size="sm"
        className="gap-1.5"
        data-testid={`promo-share-line-${tLabel}`}
      >
        <a
          href={`https://line.me/R/msg/text/?${body}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <Share2 className="w-3.5 h-3.5" />
          LINE
        </a>
      </Button>
      <Button
        asChild
        variant="outline"
        size="sm"
        className="gap-1.5"
        data-testid={`promo-share-whatsapp-${tLabel}`}
      >
        <a
          href={`https://wa.me/?text=${body}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          <MessageCircle className="w-3.5 h-3.5" />
          WhatsApp
        </a>
      </Button>
    </div>
  );
}

function VideoCard({ video }) {
  const { t } = useLang();
  const [copied, setCopied] = useState(false);

  const streamUrl = `${BACKEND_URL}/api/media/promo-videos/${video.lang}`;
  const downloadUrl = `${BACKEND_URL}/api/media/promo-videos/${video.lang}/download`;
  const shareUrl = useMemo(() => {
    try {
      return new URL(
        `/api/media/promo-videos/${video.lang}`,
        window.location.origin,
      ).toString();
    } catch {
      return streamUrl;
    }
  }, [video.lang, streamUrl]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      toast.success(t("admin.promo.link_copied"));
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error(t("admin.promo.copy_failed"));
    }
  };

  return (
    <Card
      className="border-stone-200/80 overflow-hidden"
      data-testid={`promo-card-${video.lang}`}
    >
      <CardContent className="p-5 sm:p-6 space-y-4">
        <div className="flex items-baseline justify-between gap-3">
          <h3 className="font-serif text-lg sm:text-xl text-stone-900">
            {t(video.titleKey)}
          </h3>
          <span className="font-mono text-[11px] uppercase tracking-wider text-stone-500 whitespace-nowrap">
            {video.duration} · {video.size}
          </span>
        </div>

        <div className="rounded-lg overflow-hidden bg-stone-900">
          <video
            controls
            preload="metadata"
            playsInline
            className="w-full h-auto block"
            data-testid={`promo-video-${video.lang}`}
          >
            <source src={streamUrl} type="video/mp4" />
          </video>
        </div>

        <p className="caption-label text-stone-500">{video.resolution}</p>

        <div className="flex flex-wrap gap-2">
          <Button
            asChild
            className="bg-emerald-700 hover:bg-emerald-800 text-white gap-1.5"
            size="sm"
            data-testid={`promo-download-${video.lang}`}
          >
            <a href={downloadUrl} download={video.filename}>
              <Download className="w-3.5 h-3.5" />
              {t("admin.promo.download")}
            </a>
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={copyLink}
            data-testid={`promo-copy-${video.lang}`}
          >
            <Link2 className="w-3.5 h-3.5" />
            {copied ? t("admin.promo.link_copied") : t("admin.promo.copy_link")}
          </Button>
        </div>

        <ShareButtons
          shareUrl={shareUrl}
          message={video.message}
          tLabel={video.lang}
        />

        <p className="text-xs text-stone-500 pt-2 border-t border-stone-100">
          {t("admin.promo.tip")}
        </p>
      </CardContent>
    </Card>
  );
}

export default function AdminPromoVideos() {
  const { t } = useLang();
  return (
    <div className="min-h-screen bg-stone-50">
      <CorpHeader />
      <main className="max-w-7xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1
            className="font-serif text-3xl sm:text-4xl text-stone-900"
            data-testid="promo-page-title"
          >
            {t("admin.promo.title")}
          </h1>
          <p className="caption-label mt-1.5 text-stone-500">
            {t("admin.promo.subtitle")}
          </p>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {VIDEOS.map((v) => (
            <VideoCard key={v.lang} video={v} />
          ))}
        </div>
      </main>
    </div>
  );
}
